import React, { useState } from "react";
import { BookOpen, FolderDown, ExternalLink, Link2, Lightbulb, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const books = [
  { title: "Piense y Hágase Rico", author: "Napoleón Hill", desc: "El clásico de mentalidad y riqueza. Dominio público.", url: "https://archive.org/details/piense-y-hagase-rico" },
  { title: "El Hombre Más Rico de Babilonia", author: "George S. Clason", desc: "Principios atemporales de finanzas personales.", url: "https://archive.org/details/el-hombre-mas-rico-de-babilonia" },
  { title: "La Ciencia de Hacerse Rico", author: "Wallace D. Wattles", desc: "Base de la mentalidad de abundancia.", url: "https://archive.org/details/lacienciadehacerserico" },
  { title: "Cómo Ganar Amigos e Influir sobre las Personas", author: "Dale Carnegie", desc: "Habilidades sociales clave para prospectar.", url: "https://archive.org/details/comoganaramigose0000carn" },
  { title: "El Poder del Pensamiento Positivo", author: "Norman V. Peale", desc: "Actitud ganadora para el día a día.", url: "https://archive.org/search?query=el+poder+del+pensamiento+positivo" },
  { title: "Las 48 Leyes del Poder (resumen)", author: "Robert Greene", desc: "Estrategia e influencia para líderes.", url: "https://archive.org/search?query=48+leyes+del+poder" },
];

const links = [
  { name: "Direct Selling Association (DSA)", desc: "Asociación de venta directa, estándares de la industria.", url: "https://www.dsa.org" },
  { name: "FTC: Multi-Level Marketing", desc: "Guías oficiales para operar de forma ética y legal.", url: "https://www.ftc.gov/business-guidance/resources/multi-level-marketing-businesses-pyramid-schemes" },
  { name: "Business For Home", desc: "Rankings y noticias de compañías multinivel.", url: "https://www.businessforhome.org" },
  { name: "Canva", desc: "Diseña tus piezas gráficas gratis.", url: "https://www.canva.com" },
  { name: "CapCut", desc: "Edita videos cortos para redes.", url: "https://www.capcut.com" },
];

const tips = [
  "Habla con 5 personas nuevas cada día: la prospección es un juego de números.",
  "Vende la oportunidad, no solo el producto. La gente compra transformación.",
  "Usa tu historia personal: es tu activo más poderoso y nadie puede copiarla.",
  "Haz seguimiento: el 80% de las ventas ocurren después del 5º contacto.",
  "Duplica: enseña a tu equipo a hacer lo mismo que tú haces.",
  "Nunca hagas spam. Atrae con valor y deja que te pregunten.",
];

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-purple-500/50 transition-colors">{children}</div>
);

export default function Resources() {
  const [tab, setTab] = useState("libros");
  return (
    <div data-testid="resources-page">
      <h1 className="font-display font-black tracking-tight text-3xl mb-2">Libros y Archivos</h1>
      <p className="text-zinc-400 mb-8">Tu biblioteca y recursos para crecer como líder.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121212] border border-white/10 rounded-xl p-1 mb-7">
          <TabsTrigger value="libros" data-testid="tab-libros" className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white px-4">
            <BookOpen size={16} className="mr-2" /> Área de Libros
          </TabsTrigger>
          <TabsTrigger value="archivos" data-testid="tab-archivos" className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white px-4">
            <FolderDown size={16} className="mr-2" /> Área de Archivos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="libros">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {books.map((b) => (
              <Card key={b.title} testid="book-card">
                <BookOpen className="text-yellow-400 mb-3" size={24} />
                <h3 className="font-display font-bold text-base">{b.title}</h3>
                <p className="text-xs text-purple-300 mt-0.5">{b.author}</p>
                <p className="text-sm text-zinc-400 mt-2">{b.desc}</p>
                <a href={b.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm text-purple-400 hover:text-purple-300 font-semibold">
                  <Download size={15} /> Leer / Descargar
                </a>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="archivos">
          <div className="space-y-8">
            <div>
              <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Link2 size={20} className="text-purple-400" /> Enlaces de la industria</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {links.map((l) => (
                  <Card key={l.name} testid="link-card">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{l.name}</h3>
                        <p className="text-sm text-zinc-400 mt-1">{l.desc}</p>
                      </div>
                      <a href={l.url} target="_blank" rel="noreferrer" className="shrink-0 text-purple-400 hover:text-purple-300"><ExternalLink size={18} /></a>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Lightbulb size={20} className="text-yellow-400" /> Consejos de oro</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {tips.map((t, i) => (
                  <div key={t} className="rounded-2xl border border-white/10 bg-[#121212] p-5 flex gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-yellow-400/15 text-yellow-impact flex items-center justify-center font-bold text-sm">{i + 1}</span>
                    <p className="text-sm text-zinc-300">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
